import { useEffect, useState, useCallback, useRef } from 'react'
import JSZip from 'jszip'
import { useGenerationStore } from '../store/generationStore'
import { GenerationCard } from '../components/GenerationCard'
import { ImageLightbox } from '../components/ImageLightbox'
import { MoveToProjectModal } from '../components/MoveToProjectModal'
import { SelectionToolbar } from '../components/SelectionToolbar'
import { BulkDeleteModal } from '../components/BulkDeleteModal'
import { DownloadProgressModal, type DownloadPhase } from '../components/DownloadProgressModal'
import { generationsApi } from '../lib/api'
import type { Generation, GenerationDetail } from '../types'

export function Gallery() {
  const {
    generations,
    loadingList,
    fetchGenerations,
    forceRefresh,
    loadFullGallery,
    isFullGalleryLoaded,
    loadingFullGallery,
    updateGeneration,
    deleteGeneration
  } = useGenerationStore()

  // Lightbox state
  const [lightboxOpen, setLightboxOpen] = useState(false)
  const [lightboxImages, setLightboxImages] = useState<Array<{ url: string; id: string; shotNumber: number }>>([])
  const [loadingQuickView, setLoadingQuickView] = useState(false)

  // Move to project modal state
  const [moveModalOpen, setMoveModalOpen] = useState(false)
  const [selectedGeneration, setSelectedGeneration] = useState<Generation | null>(null)

  // Multi-select state
  const [selectionMode, setSelectionMode] = useState(false)
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())

  // Bulk delete modal state
  const [deleteModalOpen, setDeleteModalOpen] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)

  // Download progress modal state
  const [downloadModalOpen, setDownloadModalOpen] = useState(false)
  const [downloadPhase, setDownloadPhase] = useState<DownloadPhase>('fetching')
  const [downloadCurrent, setDownloadCurrent] = useState(0)
  const [downloadTotal, setDownloadTotal] = useState(0)
  const downloadCancelledRef = useRef(false)

  // Handle quick view - fetch full generation details and show lightbox
  const handleQuickView = async (generation: Generation) => {
    if (generation.status !== 'COMPLETED') return

    setLoadingQuickView(true)
    try {
      const detail: GenerationDetail = await generationsApi.getOne(generation.id)
      if (detail.images && detail.images.length > 0) {
        setLightboxImages(detail.images.map((img) => ({
          url: img.url,
          id: img.id,
          shotNumber: img.shotNumber,
        })))
        setLightboxOpen(true)
      }
    } catch (err) {
      console.error('Failed to load generation details:', err)
    } finally {
      setLoadingQuickView(false)
    }
  }

  // Handle move to project
  const handleMoveToProject = (generation: Generation) => {
    setSelectedGeneration(generation)
    setMoveModalOpen(true)
  }

  // Handle after move completed
  const handleMoved = (projectId: string | null, projectName: string | null) => {
    if (selectedGeneration) {
      updateGeneration(selectedGeneration.id, { projectId, projectName })
    }
  }

  // Handle delete generation
  const handleDelete = async (generation: Generation) => {
    if (!confirm('Delete this generation? This cannot be undone.')) return
    try {
      await deleteGeneration(generation.id)
    } catch (err) {
      console.error('Failed to delete generation:', err)
    }
  }

  // Get completed generations for selection
  const completedGenerations = generations.filter(g => g.status === 'COMPLETED')

  // Selection handlers
  const toggleSelect = useCallback((id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      if (next.size === 0) {
        setSelectionMode(false)
      } else {
        setSelectionMode(true)
      }
      return next
    })
  }, [])

  const selectAll = useCallback(() => {
    setSelectedIds(new Set(completedGenerations.map(g => g.id)))
    setSelectionMode(true)
  }, [completedGenerations])

  const deselectAll = useCallback(() => {
    setSelectedIds(new Set())
    setSelectionMode(false)
  }, [])

  const cancelSelection = useCallback(() => {
    setSelectedIds(new Set())
    setSelectionMode(false)
  }, [])

  // Bulk download handler
  const handleBulkDownload = useCallback(async () => {
    if (selectedIds.size === 0) return

    setDownloadModalOpen(true)
    setDownloadPhase('fetching')
    setDownloadCurrent(0)
    setDownloadTotal(selectedIds.size)
    downloadCancelledRef.current = false

    try {
      // Phase 1: Fetch all generation details
      const selectedIdsArray = Array.from(selectedIds)
      const details: GenerationDetail[] = []

      for (let i = 0; i < selectedIdsArray.length; i++) {
        if (downloadCancelledRef.current) return
        const detail = await generationsApi.getOne(selectedIdsArray[i])
        details.push(detail)
        setDownloadCurrent(i + 1)
      }

      // Count total images
      const allImages = details.flatMap(d => d.images || [])
      if (allImages.length === 0) {
        setDownloadModalOpen(false)
        return
      }

      // Phase 2: Download all images
      setDownloadPhase('downloading')
      setDownloadCurrent(0)
      setDownloadTotal(allImages.length)

      const zip = new JSZip()

      for (let i = 0; i < allImages.length; i++) {
        if (downloadCancelledRef.current) return
        const img = allImages[i]
        try {
          const response = await fetch(img.url)
          const blob = await response.blob()
          // Create a meaningful filename
          const ext = img.url.includes('.png') ? 'png' : 'jpg'
          zip.file(`kalypso-${img.id}.${ext}`, blob)
        } catch (err) {
          console.error('Failed to download image:', img.id, err)
        }
        setDownloadCurrent(i + 1)
      }

      // Phase 3: Create ZIP
      setDownloadPhase('creating')
      setDownloadCurrent(0)
      setDownloadTotal(1)

      const zipBlob = await zip.generateAsync({ type: 'blob' })

      // Download the ZIP
      const url = URL.createObjectURL(zipBlob)
      const link = document.createElement('a')
      link.href = url
      link.download = `kalypso-${selectedIds.size}-generations.zip`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(url)

      setDownloadPhase('complete')
      setDownloadCurrent(1)
    } catch (err) {
      console.error('Bulk download failed:', err)
      setDownloadModalOpen(false)
    }
  }, [selectedIds])

  // Bulk delete handler
  const handleBulkDelete = useCallback(async () => {
    if (selectedIds.size === 0) return

    setIsDeleting(true)
    try {
      // Delete all selected generations in parallel
      await Promise.all(
        Array.from(selectedIds).map(id => deleteGeneration(id))
      )

      // Clear selection and close modal
      setSelectedIds(new Set())
      setSelectionMode(false)
      setDeleteModalOpen(false)
    } catch (err) {
      console.error('Bulk delete failed:', err)
    } finally {
      setIsDeleting(false)
    }
  }, [selectedIds, deleteGeneration])

  useEffect(() => {
    fetchGenerations()
  }, [fetchGenerations])

  return (
    <>
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
        <div className="flex flex-col gap-2">
          <h1 className="text-3xl md:text-4xl font-semibold text-text-main leading-tight">
            Gallery
          </h1>
          <p className="text-text-muted text-base">
            All your generated product photos
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => forceRefresh()}
            disabled={loadingList}
            className="size-9 flex items-center justify-center rounded-lg bg-transparent hover:bg-white text-text-muted transition-colors disabled:opacity-50"
            title="Refresh gallery"
          >
            <span className={`material-symbols-outlined text-sm ${loadingList ? 'animate-spin' : ''}`}>refresh</span>
          </button>
          <button className="size-9 flex items-center justify-center rounded-lg bg-white border border-border-color text-primary-dark shadow-sm hover:bg-gray-50">
            <span className="material-symbols-outlined text-sm">grid_view</span>
          </button>
          <button className="size-9 flex items-center justify-center rounded-lg bg-transparent hover:bg-white text-text-muted transition-colors">
            <span className="material-symbols-outlined text-sm">list</span>
          </button>
        </div>
      </div>

      {/* Gallery Grid */}
      {loadingList ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
          {[...Array(10)].map((_, i) => (
            <div
              key={i}
              className="aspect-square rounded-xl bg-surface-muted animate-pulse"
            />
          ))}
        </div>
      ) : generations.length === 0 ? (
        <div className="text-center py-16">
          <span className="material-symbols-outlined text-6xl text-text-light mb-4 block">
            photo_library
          </span>
          <h3 className="text-lg font-medium mb-2">No photos yet</h3>
          <p className="text-text-muted mb-6">
            Generate your first product photo from the dashboard
          </p>
          <a href="/" className="btn btn-primary">
            Go to Dashboard
          </a>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {generations.map((gen) => (
              <GenerationCard
                key={gen.id}
                generation={gen}
                onQuickView={handleQuickView}
                onMoveToProject={handleMoveToProject}
                onDelete={handleDelete}
                selectionMode={selectionMode}
                isSelected={selectedIds.has(gen.id)}
                onToggleSelect={gen.status === 'COMPLETED' ? toggleSelect : undefined}
              />
            ))}
          </div>

          {/* Load Full Gallery Button */}
          {!isFullGalleryLoaded && generations.length >= 10 && (
            <div className="mt-12 flex justify-center">
              <button
                onClick={() => loadFullGallery()}
                disabled={loadingFullGallery}
                className="group relative overflow-hidden rounded-2xl bg-white border border-border-color px-8 py-4 shadow-sm transition-all duration-300 hover:shadow-lg hover:border-primary/30 hover:-translate-y-0.5 disabled:opacity-70 disabled:cursor-not-allowed"
              >
                {/* Subtle gradient background on hover */}
                <div className="absolute inset-0 bg-gradient-to-r from-primary/5 via-transparent to-accent/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

                <div className="relative flex items-center gap-3">
                  {loadingFullGallery ? (
                    <>
                      <div className="w-5 h-5 border-2 border-primary/20 border-t-primary rounded-full animate-spin" />
                      <span className="text-text-main font-medium">Loading all photos...</span>
                    </>
                  ) : (
                    <>
                      <span className="material-symbols-outlined text-primary text-xl transition-transform duration-300 group-hover:scale-110">
                        expand_circle_down
                      </span>
                      <span className="text-text-main font-medium">Load Full Gallery</span>
                      <span className="text-text-muted text-sm ml-1">
                        · View all your photos
                      </span>
                    </>
                  )}
                </div>
              </button>
            </div>
          )}

          {/* Indicator when full gallery is loaded */}
          {isFullGalleryLoaded && (
            <div className="mt-8 flex justify-center">
              <div className="flex items-center gap-2 text-text-muted text-sm">
                <span className="material-symbols-outlined text-primary/60 text-base">check_circle</span>
                <span>Showing all {generations.length} photos</span>
              </div>
            </div>
          )}
        </>
      )}

      {/* Loading overlay for quick view */}
      {loadingQuickView && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center">
          <div className="bg-white rounded-xl p-6 shadow-lg flex items-center gap-3">
            <div className="w-6 h-6 border-2 border-primary/20 border-t-primary rounded-full animate-spin" />
            <span className="text-text-main font-medium">Loading...</span>
          </div>
        </div>
      )}

      {/* Full-screen Lightbox */}
      <ImageLightbox
        isOpen={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
        images={lightboxImages}
        initialIndex={0}
      />

      {/* Move to Project Modal */}
      {selectedGeneration && (
        <MoveToProjectModal
          isOpen={moveModalOpen}
          onClose={() => setMoveModalOpen(false)}
          generation={selectedGeneration}
          onMoved={handleMoved}
        />
      )}

      {/* Selection Toolbar */}
      {selectionMode && selectedIds.size > 0 && (
        <SelectionToolbar
          count={selectedIds.size}
          totalCount={completedGenerations.length}
          onSelectAll={selectAll}
          onDeselectAll={deselectAll}
          onDownload={handleBulkDownload}
          onDelete={() => setDeleteModalOpen(true)}
          onCancel={cancelSelection}
        />
      )}

      {/* Bulk Delete Modal */}
      <BulkDeleteModal
        isOpen={deleteModalOpen}
        count={selectedIds.size}
        onConfirm={handleBulkDelete}
        onCancel={() => setDeleteModalOpen(false)}
        isDeleting={isDeleting}
      />

      {/* Download Progress Modal */}
      <DownloadProgressModal
        isOpen={downloadModalOpen}
        phase={downloadPhase}
        current={downloadCurrent}
        total={downloadTotal}
        onCancel={() => {
          downloadCancelledRef.current = true
          setDownloadModalOpen(false)
        }}
      />
    </>
  )
}
